import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import { useGoogleAnalytics, useMetaPixel } from '@/utils/analytics';
import HomePage from '@/pages/HomePage';
import AboutPage from '@/pages/AboutPage';
import CoursesPage from '@/pages/CoursesPage';
import InstructorsPage from '@/pages/InstructorsPage';
import BlogPage from '@/pages/BlogPage';
import BlogDetailPage from '@/pages/BlogDetailPage';
import ContactPage from '@/pages/ContactPage';
import ExamGuidePage from '@/pages/ExamGuidePage';
import TestimonialsPage from '@/pages/TestimonialsPage';
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminBlogs from '@/pages/admin/AdminBlogs';
import AdminClients from '@/pages/admin/AdminClients';
import AdminInstructors from '@/pages/admin/AdminInstructors';
import AdminTestimonials from '@/pages/admin/AdminTestimonials';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminExamRoutes from '@/pages/admin/AdminExamRoutes';
import AdminCategories from '@/pages/admin/AdminCategories';
import AdminHomeFeatures from '@/pages/admin/AdminHomeFeatures';
import AdminAboutContent from '@/pages/admin/AdminAboutContent';
import AdminCoursesContent from '@/pages/admin/AdminCoursesContent';
import AdminGallery from '@/pages/admin/AdminGallery';
import AdminImages from '@/pages/admin/AdminImages';
import AdminPopups from '@/pages/admin/AdminPopups';
import AdminInquiries from '@/pages/admin/AdminInquiries';
import { AuthProvider } from '@/contexts/AuthContext';
import { SiteSettingsProvider, useSiteSettings } from '@/contexts/SiteSettingsContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import PopupModal from '@/components/PopupModal';
import '@/App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRoutes() {
  const { trackingIds } = useSiteSettings();

  // 初始化追蹤
  useGoogleAnalytics(trackingIds.googleAnalyticsId);
  useMetaPixel(trackingIds.metaPixelId);

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/courses" element={<CoursesPage />} />
      <Route path="/instructors" element={<InstructorsPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:id" element={<BlogDetailPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/exam-guide" element={<ExamGuidePage />} />
      <Route path="/testimonials" element={<TestimonialsPage />} />
      
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/blogs"
        element={
          <ProtectedRoute>
            <AdminBlogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute>
            <AdminClients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/instructors"
        element={
          <ProtectedRoute>
            <AdminInstructors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/testimonials"
        element={
          <ProtectedRoute>
            <AdminTestimonials />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute>
            <AdminSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/exam-routes"
        element={
          <ProtectedRoute>
            <AdminExamRoutes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/categories"
        element={
          <ProtectedRoute>
            <AdminCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/popups"
        element={<ProtectedRoute><AdminPopups /></ProtectedRoute>}
      />
      <Route
        path="/admin/images"
        element={
          <ProtectedRoute>
            <AdminImages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/gallery"
        element={
          <ProtectedRoute>
            <AdminGallery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/home-features"
        element={
          <ProtectedRoute>
            <AdminHomeFeatures />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/about"
        element={
          <ProtectedRoute>
            <AdminAboutContent />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/courses-content"
        element={
          <ProtectedRoute>
            <AdminCoursesContent />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inquiries"
        element={<ProtectedRoute><AdminInquiries /></ProtectedRoute>}
      />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SiteSettingsProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppRoutes />
            <PopupModal />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </SiteSettingsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;